import { HttpInterceptorFn, HttpResponse, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { inject } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { finalize, tap } from 'rxjs';

// Tokens to customize interceptor behavior per-request if needed
export const BYPASS_SPINNER = new HttpContextToken<boolean>(() => false);
export const BYPASS_TOASTER = new HttpContextToken<boolean>(() => false);

/**
 * The overlay is only shown when a request is really slow.
 * Without this delay, fast requests (local assets, cached calls, quick API responses)
 * produce a show/hide pair a few milliseconds apart. ngx-spinner schedules both
 * show() and hide() with a 10ms timer, so such a pair can happen inside a single
 * animation frame - the place where its leave animation gets stuck and leaves an
 * invisible full screen overlay behind that blocks every click.
 */
const SHOW_DELAY = 250;

/**
 * Fail-safe re-check interval.
 *
 * The watchdog ONLY guards against a leftover overlay while nothing is pending.
 * It must never hide the overlay while a request is still in flight, so when
 * requests are still active it simply re-arms itself and checks again later.
 */
const WATCHDOG_DELAY = 15000;

/** Delay used to verify that the overlay really disappeared from the DOM. */
const OVERLAY_VERIFY_DELAY = 600;

let activeRequestsCount = 0;
let showTimer: ReturnType<typeof setTimeout> | null = null;
let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
let verifyTimer: ReturnType<typeof setTimeout> | null = null;

function clearTimer(timer: ReturnType<typeof setTimeout> | null): null {
  if (timer !== null) {
    clearTimeout(timer);
  }
  return null;
}

/**
 * Last-resort safety net.
 *
 * The ngx-spinner overlay is `position: fixed` and covers the whole viewport, so a
 * leftover (invisible) overlay silently swallows every click of the page and makes the
 * app look frozen. Whenever the loading state is finished we make sure that such an
 * element cannot intercept pointer events. Visible overlays keep blocking as before.
 */
function releaseInvisibleOverlays(): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.querySelectorAll<HTMLElement>('ngx-spinner .ngx-spinner-overlay').forEach((overlay) => {
    overlay.style.pointerEvents = getComputedStyle(overlay).opacity === '0' ? 'none' : '';
  });
}

function hideSpinner(spinner: NgxSpinnerService): void {
  showTimer = clearTimer(showTimer);
  watchdogTimer = clearTimer(watchdogTimer);
  spinner.hide();
  // Re-check once the (debounced) hide has been applied.
  verifyTimer = clearTimer(verifyTimer);
  verifyTimer = setTimeout(() => {
    verifyTimer = null;
    releaseInvisibleOverlays();
  }, OVERLAY_VERIFY_DELAY);
}

export function forceResetLoader(spinner?: NgxSpinnerService) {
  activeRequestsCount = 0;
  showTimer = clearTimer(showTimer);
  watchdogTimer = clearTimer(watchdogTimer);
  spinner?.hide();
  releaseInvisibleOverlays();
}

/**
 * Fail-safe watchdog.
 */
function armWatchdog(spinner: NgxSpinnerService): void {
  watchdogTimer = clearTimer(watchdogTimer);
  watchdogTimer = setTimeout(() => {
    watchdogTimer = null;
    if (activeRequestsCount > 0) {
      armWatchdog(spinner);
      return;
    }
    forceResetLoader(spinner);
  }, WATCHDOG_DELAY);
}

function requestStarted(spinner: NgxSpinnerService): void {
  activeRequestsCount++;
  if (activeRequestsCount !== 1 || showTimer !== null) {
    return;
  }

  showTimer = setTimeout(() => {
    showTimer = null;
    if (activeRequestsCount === 0) {
      return;
    }
    spinner.show();
    armWatchdog(spinner);
  }, SHOW_DELAY);
}

function requestFinished(spinner: NgxSpinnerService): void {
  activeRequestsCount = Math.max(0, activeRequestsCount - 1);
  if (activeRequestsCount === 0) {
    hideSpinner(spinner);
  }
}

/**
 * Requests that must never drive the global loading overlay: static assets, the app
 * config file, images and explicit bypasses.
 */
function isBackgroundCall(
  url: string,
  context: { get: (token: HttpContextToken<boolean>) => boolean }
): boolean {
  const lowerUrl = url.toLowerCase();

  return (
    lowerUrl.includes('assets/') ||
    lowerUrl.includes('/getimage/') ||
    lowerUrl.includes('/image/') ||
    lowerUrl.includes('/get-profile-image/') ||
    lowerUrl.endsWith('.json') ||
    lowerUrl.endsWith('.png') ||
    lowerUrl.endsWith('.jpg') ||
    lowerUrl.endsWith('.jpeg') ||
    lowerUrl.endsWith('.gif') ||
    lowerUrl.endsWith('.svg') ||
    lowerUrl.endsWith('.ico') ||
    lowerUrl.endsWith('.css') ||
    lowerUrl.endsWith('.js') ||
    lowerUrl.endsWith('.woff') ||
    lowerUrl.endsWith('.woff2') ||
    context.get(BYPASS_SPINNER)
  );
}

export const loadingInterceptor: HttpInterceptorFn = (req, next) => {
  const spinner = inject(NgxSpinnerService);
  const toastr = inject(ToastrService);

  const isBackground = isBackgroundCall(req.url, req.context);

  if (!isBackground) {
    requestStarted(spinner);
  }

  return next(req).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          // Toast success on modify requests (POST, PUT, DELETE) if not bypassed
          if (!req.context.get(BYPASS_TOASTER) && ['POST', 'PUT', 'DELETE'].includes(req.method)) {
            const isCustomHandled =
              req.url.includes('/auth/login') ||
              req.url.includes('/auth/register') ||
              req.url.includes('/registration/save');
            const body = event.body as any;
            const successMsg = body?.msg || body?.message || body?.msgText;

            if (!isCustomHandled) {
              if (successMsg) {
                toastr.success(successMsg);
              } else {
                if (req.method === 'DELETE' || req.url.includes('/delete')) {
                  toastr.success('Deleted successfully');
                } else if (req.method === 'POST' || req.url.includes('/save') || req.url.includes('/add')) {
                  toastr.success('Saved successfully');
                } else if (req.method === 'PUT' || req.url.includes('/update')) {
                  toastr.success('Updated successfully');
                }
              }
            }
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        if (!req.context.get(BYPASS_TOASTER)) {
          console.error('API Error details:', error);
          let errorMsg = error.error?.msg || error.error?.message;
          if (!errorMsg && typeof error.error === 'string') {
            try {
              const parsed = JSON.parse(error.error);
              errorMsg = parsed?.msg || parsed?.message;
            } catch {}
          }
          if (!errorMsg) {
            errorMsg = error.error?.error || error.message || 'Something went wrong, please try again';
          }
          toastr.error(errorMsg);
        }
      }
    }),
    finalize(() => {
      if (!isBackground) {
        requestFinished(spinner);
      }
    })
  );
};
